// invodiff-input.jsx — data input state with dual drop zones + vendor selector.
import { pdfSourceOf } from './doc-provenance-core.js';
import { ConfirmModal } from './invodiff-jobs.jsx';
import React from 'react';
import { firebase, fbAuth, fbDb, fbStorage } from './firebase.js';
import {
  fmtUSD, fmtDelta, safeUrl, firestoreSafe, toCSV, downloadFile, relativeTime,
  invoiceDateOf, invoiceInRange, dateRangePresets, useFocusTrap,
  ROLES, ROLE_LABEL, ROLE_COLOR,
  usesSubInvoices, useJobsStore, useJobData, invoiceStore, deleteJobDeep, useVendorRules,
  normalizeUOM, isFootageUOM, stickFeet, itemFootage, convertToQuoteBasis, summarizeMatch,
  isAutoSku, itemKeyOf, levenshtein, fracToNum, normalizeForMatch, matchNormalize, matchLineItems,
  aggregateInvoices, compareInvoices, materialState, classifyRows, MATERIAL_STATE_META,
  getJobTrades, getSegments, lineTotal, subtotal, tax, grandTotal, pricesDiffer,
  statedInvoiceTotal, statedInvoiceTax, invoiceSpend,
  SAMPLE_VENDORS, INVOICE_A, INVOICE_B, SEED_VENDOR_RULES,
} from './invodiff-data.jsx';
import { parseInvoiceFile, parseInvoiceText, lineTypeOf } from './invoice-parser.js';
import { rememberLocalFile, attachLocalFile } from './doc-provenance-core.js';
import { DocProvenanceModal } from './doc-provenance.jsx';

function Icon({ name, size = 16, className = '', style = {} }) {
  // UX #1 (a11y): icons are decorative — hide them from screen readers and the
  // tab order so labels aren't doubled and focus stays on real controls.
  const common = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none',
    stroke: 'currentColor', strokeWidth: 1.6, strokeLinecap: 'round', strokeLinejoin: 'round',
    'aria-hidden': true, focusable: false,
    className, style };
  switch (name) {
    case 'upload': return <svg {...common}><path d="M12 16V4M12 4l-5 5M12 4l5 5M4 20h16"/></svg>;
    case 'file':   return <svg {...common}><path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"/><path d="M14 3v6h6"/></svg>;
    case 'paste':  return <svg {...common}><rect x="8" y="3" width="8" height="4" rx="1"/><path d="M16 5h2a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h2"/></svg>;
    case 'arrow':  return <svg {...common}><path d="M5 12h14M13 6l6 6-6 6"/></svg>;
    case 'check':  return <svg {...common}><path d="M5 12l5 5L20 7"/></svg>;
    case 'x':      return <svg {...common}><path d="M6 6l12 12M18 6L6 18"/></svg>;
    case 'flag':   return <svg {...common}><path d="M4 21V4M4 4h12l-2 4 2 4H4"/></svg>;
    case 'spark':  return <svg {...common}><path d="M12 3v4M12 17v4M3 12h4M17 12h4M5.6 5.6l2.8 2.8M15.6 15.6l2.8 2.8M5.6 18.4l2.8-2.8M15.6 8.4l2.8-2.8"/></svg>;
    case 'history':return <svg {...common}><path d="M3 12a9 9 0 1 0 3-6.7"/><path d="M3 4v5h5"/><path d="M12 7v5l3 2"/></svg>;
    case 'chev':   return <svg {...common}><path d="M9 6l6 6-6 6"/></svg>;
    case 'eye':     return <svg {...common}><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z"/><circle cx="12" cy="12" r="3"/></svg>;
    case 'eye-off': return <svg {...common}><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>;
    case 'delta':  return <svg {...common}><path d="M12 4 3 20h18z"/></svg>;
    case 'pct':    return <svg {...common}><path d="M19 5 5 19"/><circle cx="7.5" cy="7.5" r="2"/><circle cx="16.5" cy="16.5" r="2"/></svg>;
    case 'rule':   return <svg {...common}><path d="M4 6h16M4 12h10M4 18h16"/></svg>;
    case 'layers': return <svg {...common}><path d="M12 2 2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>;
    case 'grid':   return <svg {...common}><rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/><rect x="3" y="14" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/></svg>;
    case 'menu':   return <svg {...common}><path d="M3 6h18M3 12h18M3 18h18"/></svg>;
    case 'plus':   return <svg {...common}><path d="M12 5v14M5 12h14"/></svg>;
    case 'reset':   return <svg {...common}><path d="M3 12a9 9 0 1 0 3-6.7"/><path d="M3 4v5h5"/></svg>;
    case 'save':    return <svg {...common}><path d="M5 3h11l3 3v15H5z"/><path d="M8 3v6h8V3M8 21v-8h8v8"/></svg>;
    case 'trash':   return <svg {...common}><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>;
    case 'archive': return <svg {...common}><rect x="2" y="3" width="20" height="5" rx="1"/><path d="M4 8v11a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8"/><line x1="10" y1="13" x2="14" y2="13"/></svg>;
    case 'search':  return <svg {...common}><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>;
    case 'settings': return <svg {...common}><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>;
    case 'pencil':   return <svg {...common}><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>;
    case 'external': return <svg {...common}><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>;
    case 'download': return <svg {...common}><path d="M12 3v12M8 11l4 4 4-4M4 21h16"/></svg>;
    default: return null;
  }
}

// ── DropZone ────────────────────────────────────────────────────────────
function DropZone({ side, value, onChange, label, accent = false, jobId = null }) {
  const [over, setOver]         = React.useState(false);
  const [mode, setMode]         = React.useState(value ? 'loaded' : 'idle');
  const [parsing, setParsing]   = React.useState(false);
  const [parseErr, setParseErr] = React.useState(null);
  const fileInputRef = React.useRef(null);
  const pasteRef     = React.useRef(null);

  React.useEffect(() => {
    if (value && mode !== 'loaded') setMode('loaded');
    if (!value && mode === 'loaded') setMode('idle');
  }, [value]);

  const handleFile = async (file) => {
    if (!file) return;
    setParsing(true);
    setParseErr(null);
    try {
      const invoice = await parseInvoiceFile(file);
      let fileUrl = null;
      if (jobId && jobId !== '_sample' && window.fbStorage) {
        try {
          const ref = fbStorage.ref(`jobs/${jobId}/quote/${Date.now()}_${file.name}`);
          await ref.put(file);
          fileUrl = await ref.getDownloadURL();
        } catch (e) {
          console.warn('Storage upload failed:', e);
        }
      }
      // _localFile lets the provenance viewer open the PDF without a round
      // trip (and when storage upload failed) — session-only, never persisted.
      onChange({ ...invoice, _fileUrl: fileUrl, _fileName: file.name, _localFile: file });
    } catch (e) {
      setParseErr(e.message || 'Could not parse this file.');
    } finally {
      setParsing(false);
    }
  };

  const handlePaste = async () => {
    const text = pasteRef.current?.value || '';
    if (text.trim().length < 10) return;
    setParsing(true);
    setParseErr(null);
    try {
      const invoice = await parseInvoiceText(text);
      onChange(invoice);
    } catch (e) {
      setParseErr(e.message || 'Could not parse pasted text.');
    } finally {
      setParsing(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setOver(false);
    handleFile(e.dataTransfer.files[0]);
  };

  const onFileSelect = (e) => {
    handleFile(e.target.files[0]);
    e.target.value = '';
  };

  const clear = () => { onChange(null); setMode('idle'); setParseErr(null); };

  return (
    <div className="dropzone-wrap">
      <div className="dz-hd">
        <div className="dz-tag">
          <span className="dz-letter" style={{ background: side === 'A' ? 'var(--surface-3)' : 'oklch(from var(--accent) l c h / 0.18)', color: side === 'A' ? 'var(--fg-muted)' : 'var(--accent)' }}>{side}</span>
          <div>
            <div className="dz-label">{label}</div>
            <div className="dz-sub">{side === 'A' ? 'Purchase order / baseline' : 'Incoming invoice to verify'}</div>
          </div>
        </div>
        {value && (
          <button className="btn btn-ghost btn-sm" onClick={clear}>
            <Icon name="x" size={13}/> Clear
          </button>
        )}
      </div>

      <input
        ref={fileInputRef} type="file"
        accept=".pdf,.xlsx,.xls,.csv,.tsv,.txt,text/*,application/pdf"
        style={{ display: 'none' }} onChange={onFileSelect}
      />

      {!value ? (
        <div
          className={`dropzone ${over ? 'is-over' : ''} ${parsing ? 'is-parsing' : ''}`}
          onDragOver={(e) => { e.preventDefault(); setOver(true); }}
          onDragLeave={() => setOver(false)}
          onDrop={onDrop}
        >
          {parsing ? (
            <>
              <div className="dz-spinner"></div>
              <div className="dz-title">Extracting invoice data…</div>
              <div className="dz-hint">Reading line items and totals</div>
            </>
          ) : (
            <>
              <div className="dz-icon-wrap"><Icon name="upload" size={20} /></div>
              <div className="dz-title">Drop a PDF, Excel, CSV, or text file</div>
              {parseErr && <div className="dz-error">{parseErr}</div>}
              <div className="dz-or">— or —</div>
              <div className="dz-actions">
                <button className="btn btn-sm" onClick={() => fileInputRef.current?.click()}>
                  <Icon name="file" size={13}/> Browse file
                </button>
                <button className="btn btn-sm btn-ghost" onClick={() => { setMode('paste'); setParseErr(null); }}>
                  <Icon name="paste" size={13}/> Paste text
                </button>
                <button className="btn btn-sm btn-ghost" onClick={() => onChange(INVOICE_A)}>
                  Use sample
                </button>
              </div>
              {mode === 'paste' && (
                <>
                  <textarea
                    ref={pasteRef}
                    className="textarea dz-paste"
                    placeholder={`Paste invoice text for ${label}…`}
                    onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handlePaste(); }}
                  />
                  <button className="btn btn-primary btn-sm" onClick={handlePaste}>
                    <Icon name="spark" size={13}/> Extract data
                  </button>
                </>
              )}
            </>
          )}
        </div>
      ) : (
        <LoadedFileCard invoice={value} side={side} />
      )}
    </div>
  );
}

function LoadedFileCard({ invoice, side }) {
  const [showRaw, setShowRaw]   = React.useState(false);
  const [showProv, setShowProv] = React.useState(false);
  // Authoritative billed amount as printed (incl. tax/shipping/adjustments).
  const total    = statedInvoiceTotal(invoice);
  const hasRaw   = !!invoice._rawText;
  const hasFile  = !!invoice._fileUrl;
  const hasPdf   = !!pdfSourceOf(invoice);
  const warn     = invoice._parseWarn;

  return (
    <div className="loaded">
      <div className="loaded-hd">
        <div className="loaded-id">
          <Icon name="file" size={14} />
          <span className="mono">{invoice._fileName || invoice.id}</span>
        </div>
        <div className="loaded-hd-r">
          {hasFile && (
            <a
              className="btn btn-ghost btn-sm loaded-raw-btn"
              href={safeUrl(invoice._fileUrl)} /* audit #7: block javascript:/data: URLs */
              target="_blank"
              rel="noopener noreferrer"
              title="Open original file"
            >
              <Icon name="external" size={12}/> Original
            </a>
          )}
          {hasPdf && (
            <button
              className="btn btn-ghost btn-sm loaded-raw-btn"
              onClick={() => setShowProv(true)}
              title="Highlight each line item on the original PDF"
            >
              <Icon name="search" size={12}/> Trace items
            </button>
          )}
          {hasRaw && (
            <button
              className={`btn btn-ghost btn-sm loaded-raw-btn ${showRaw ? 'is-on' : ''}`}
              onClick={() => setShowRaw((v) => !v)}
              title="View extracted source text"
            >
              <Icon name="eye" size={12}/>{showRaw ? 'Hide source' : 'View source'}
            </button>
          )}
          <span className="status status-ok"><span className="status-dot"/> Parsed</span>
        </div>
      </div>

      {showRaw ? (
        <div className="loaded-raw">
          <div className="loaded-raw-label">Extracted source text — sent to Claude for parsing</div>
          <pre className="loaded-raw-pre">{invoice._rawText}</pre>
        </div>
      ) : (
        <>
          <div className="loaded-rows">
            <div className="lr"><span className="lr-k">Vendor</span><span className="lr-v">{invoice.vendor}</span></div>
            <div className="lr"><span className="lr-k">Date</span><span className="lr-v mono">{invoice.date}</span></div>
            <div className="lr"><span className="lr-k">PO ref</span><span className="lr-v mono">{invoice.poRef}</span></div>
            <div className="lr"><span className="lr-k">Line items</span><span className="lr-v num">{invoice.items.length}</span></div>
            <div className="lr lr-total">
              <span className="lr-k">Grand total</span>
              <span className="lr-v num">{fmtUSD(total)}</span>
            </div>
            {warn && (
              <div className="loaded-parse-warn">
                <Icon name="flag" size={11} />
                Parsed line items sum to {fmtUSD(warn.computedSubtotal)} but invoice states {fmtUSD(warn.statedSubtotal)} before tax — some unit prices may be incorrect.
              </div>
            )}
          </div>
          <div className="loaded-preview">
            {invoice.items.slice(0, 3).map((it) => (
              <div key={it.sku} className="lp-row">
                <span className="mono" style={{ color: 'var(--fg-dim)' }}>{it.sku}</span>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.desc}</span>
                <span className="num" style={{ color: 'var(--fg-muted)' }}>{fmtUSD(lineTotal(it))}</span>
              </div>
            ))}
            {invoice.items.length > 3 && (
              <div className="lp-more">+{invoice.items.length - 3} more line item{invoice.items.length - 3 === 1 ? '' : 's'}</div>
            )}
          </div>
        </>
      )}
      {showProv && <DocProvenanceModal invoice={invoice} onClose={() => setShowProv(false)} />}
    </div>
  );
}

// ── AddInvoiceZone ──────────────────────────────────────────────────────
function AddInvoiceZone({ onAdd, jobId = null }) {
  const [over, setOver]           = React.useState(false);
  const [parsing, setParsing]     = React.useState(false);
  const [progress, setProgress]   = React.useState(null); // { current, total }
  const [mode, setMode]           = React.useState('idle');
  const [err, setErr]             = React.useState(null);
  const fileRef                   = React.useRef(null);
  const pasteRef                  = React.useRef(null);

  const handleFiles = async (files) => {
    if (!files.length) return;
    setParsing(true); setErr(null);
    setProgress(files.length > 1 ? { current: 0, total: files.length } : null);
    for (let i = 0; i < files.length; i++) {
      if (files.length > 1) setProgress({ current: i + 1, total: files.length });
      try {
        const file = files[i];
        const invoice = await parseInvoiceFile(file);
        let fileUrl = null;
        if (jobId && jobId !== '_sample' && window.fbStorage) {
          try {
            const ref = fbStorage.ref(`jobs/${jobId}/invoices/${Date.now()}_${file.name}`);
            await ref.put(file);
            fileUrl = await ref.getDownloadURL();
          } catch (e) {
            console.warn('Storage upload failed:', e);
          }
        }
        await onAdd({ ...invoice, _fileUrl: fileUrl, _fileName: file.name, _localFile: file });
      } catch (e) {
        setErr(e.message || `Could not parse ${files[i].name}.`);
      }
    }
    setParsing(false);
    setProgress(null);
    setMode('idle');
  };

  const handlePaste = async () => {
    const text = pasteRef.current?.value || '';
    if (text.trim().length < 10) return;
    setParsing(true); setErr(null);
    try {
      await onAdd(await parseInvoiceText(text));
      setMode('idle');
    } catch (e) { setErr(e.message || 'Could not parse text.'); }
    finally { setParsing(false); }
  };

  return (
    <div
      className={`aiz ${over ? 'is-over' : ''} ${parsing ? 'is-parsing' : ''}`}
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); handleFiles(Array.from(e.dataTransfer.files)); }}
    >
      <input ref={fileRef} type="file" multiple
        accept=".pdf,.xlsx,.xls,.csv,.tsv,.txt,text/*,application/pdf"
        style={{ display: 'none' }}
        onChange={(e) => { handleFiles(Array.from(e.target.files)); e.target.value = ''; }}
      />
      {parsing ? (
        <>
          <div className="aiz-spin"></div>
          <span className="aiz-hint">
            {progress ? `Extracting ${progress.current} / ${progress.total}…` : 'Extracting invoice…'}
          </span>
        </>
      ) : (
        <>
          <Icon name="upload" size={15} style={{ color: 'var(--fg-dim)' }} />
          <span className="aiz-hint">Drop files or</span>
          <button className="btn btn-sm" onClick={() => fileRef.current?.click()}>Browse</button>
          <button className="btn btn-sm btn-ghost" onClick={() => setMode(mode === 'paste' ? 'idle' : 'paste')}>
            Paste text
          </button>
          {mode === 'paste' && (
            <>
              <textarea
                ref={pasteRef}
                className="textarea aiz-paste"
                aria-label="Invoice text"
                placeholder="Paste invoice text…"
                onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handlePaste(); }}
              />
              <button className="btn btn-primary btn-sm" onClick={handlePaste}>
                <Icon name="spark" size={13} /> Extract
              </button>
            </>
          )}
        </>
      )}
      {err && <div className="aiz-err">{err}</div>}
    </div>
  );
}

// ── InputView ───────────────────────────────────────────────────────────
// Unified job view: left = document management, right = live budget tracker.
// Strip session-only fields (`_rawText`, `_localFile`) and normalize
// `undefined`→`null` before persisting an invoice/quote. Delegates to the
// shared firestoreSafe walk (see invodiff-data.jsx) — pure, so it lives at
// module scope rather than being rebuilt on every InputView render.
const clean = (obj) => window.firestoreSafe(obj, ['_rawText', '_localFile']);

// One invoice's headline spend — the document's STATED grand total (which nets
// adjustments like restock fees the subtotal omits); a REGULAR invoice on a
// tax-exempt job drops its tax (credits keep their net total). See invoiceSpend
// in invodiff-data.jsx.
const invoiceTotal = (inv, taxExempt) => invoiceSpend(inv, taxExempt);

const INV_SORTS = [
  { key: 'recent', label: 'Newest first' },
  { key: 'oldest', label: 'Oldest first' },
  { key: 'total',  label: 'Highest total' },
  { key: 'vendor', label: 'Vendor (A–Z)' },
];
// Comparators operate on { inv, idx } so original indices survive sorting.
const INV_CMP = {
  recent: (a, b) => (b.inv.date || '').localeCompare(a.inv.date || '') || b.idx - a.idx,
  oldest: (a, b) => (a.inv.date || '').localeCompare(b.inv.date || '') || a.idx - b.idx,
  total:  (a, b) => invoiceTotal(b.inv) - invoiceTotal(a.inv),
  vendor: (a, b) => (a.inv.vendor || '').localeCompare(b.inv.vendor || ''),
};
const INV_PAGE = 40; // initial rows shown before "show all"

function InputView({ job, userId, vendorRules, userInfo, focusInvoice, onFocusConsumed }) {
  const [quote, setQuote]            = React.useState(null);
  const [quotesByTrade, setQuotesByTrade] = React.useState({}); // per-trade quotes (multi-trade jobs)
  const [trades, setTrades]          = React.useState(() => getJobTrades(job));
  const [uploadTrade, setUploadTrade] = React.useState(null); // which trade the next invoice/quote is for
  const [invoices, setInvoices]      = React.useState([]);
  const [synced, setSynced]          = React.useState(false);
  const [submittedIds, setSubmitted] = React.useState(new Set());
  const [submitErr, setSubmitErr]    = React.useState(null);
  const [saveErr,    setSaveErr]     = React.useState(null);
  const [provIdx,   setProvIdx]      = React.useState(null); // invoice index open in the provenance viewer
  const [invQuery,  setInvQuery]     = React.useState('');
  const [invSort,   setInvSort]      = React.useState('recent');
  const [invShowAll, setInvShowAll]  = React.useState(false);
  const [highlightInv, setHighlightInv] = React.useState(null); // invoice id to pulse after a price-history drill-in
  const [taxExempt,  setTaxExempt]   = React.useState(!!job.taxExempt);
  const [confirmCfg, setConfirmCfg]  = React.useState(null); // in-app confirm dialog config
  const [reanalyzingIdx, setReanalyzing] = React.useState(null); // invoice index being re-analyzed

  // UX #12: surface persistence failures instead of swallowing them in the
  // console. Firestore's offline cache rolls the local change back to the
  // server value automatically (the live listener below re-emits it).
  const handleWriteErr = (e) => {
    console.error(e);
    setSaveErr("Couldn't save your change — check your connection and try again.");
  };

  // Search + sort the invoice list while keeping each invoice's ORIGINAL array
  // index (the hide/remove/trace handlers are index-based on `invoices`).
  const invView = React.useMemo(() => {
    const q = invQuery.trim().toLowerCase();
    const indexed = invoices.map((inv, idx) => ({ inv, idx }));
    const filtered = q
      ? indexed.flatMap((x) =>
          `${x.inv._fileName || ''} ${x.inv.id || ''} ${x.inv.vendor || ''} ${x.inv.date || ''} ${x.inv.trade || ''}`
            .toLowerCase().includes(q) ? [x] : [])
      : indexed;
    filtered.sort(INV_CMP[invSort] || INV_CMP.recent);
    return filtered;
  }, [invoices, invQuery, invSort]);

  const invSummary = React.useMemo(() => {
    let total = 0, hidden = 0, minD = '', maxD = '';
    for (const inv of invoices) {
      if (inv._hidden) { hidden++; continue; }
      total += invoiceTotal(inv, taxExempt);
      if (inv.date) {
        if (!minD || inv.date < minD) minD = inv.date;
        if (!maxD || inv.date > maxD) maxD = inv.date;
      }
    }
    return { total: +total.toFixed(2), hidden, count: invoices.length, minD, maxD };
  }, [invoices, taxExempt]);

  // Deep-link from Price History: reveal, scroll to, and briefly pulse the
  // invoice the estimator drilled into. consumedRef guards re-renders so it only
  // fires once per drill-in.
  const focusConsumedRef = React.useRef(null);
  React.useEffect(() => {
    if (!focusInvoice || !synced || focusConsumedRef.current === focusInvoice) return;
    focusConsumedRef.current = focusInvoice;
    setInvShowAll(true);
    setHighlightInv(focusInvoice);
    const raf = requestAnimationFrame(() => {
      const el = document.getElementById('invrow-' + focusInvoice);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
    const timer = setTimeout(() => {
      setHighlightInv(null);
      if (onFocusConsumed) onFocusConsumed();
    }, 2600);
    return () => { cancelAnimationFrame(raf); clearTimeout(timer); };
  }, [focusInvoice, synced]);

  const isSample = job.id === '_sample';
  const jobRef   = React.useMemo(
    () => fbDb.collection('jobs').doc(job.id),
    [job.id]
  );

  // UX #11: jobs are a shared team pool, so subscribe live instead of a
  // one-time read — a teammate's invoice/quote edits now appear without a
  // manual refresh. The dual-shape hook (invodiff-data.jsx) resolves the
  // invoices from whichever storage shape the job uses; `useSub` routes this
  // view's writes to the same shape.
  const live   = useJobData(isSample ? null : job.id);
  const useSub = !isSample && usesSubInvoices(live.data);
  React.useEffect(() => {
    if (isSample) {
      setQuote(INVOICE_A);
      setInvoices([INVOICE_B]);
      setSynced(true);
      return;
    }
    if (!live.synced) { setSynced(false); return; }
    if (live.exists) {
      const d = live.data;
      setQuote(d.quote ?? null);
      setQuotesByTrade(d.quotesByTrade ?? {});
      setTrades(getJobTrades(d));
      setInvoices(live.invoices);
      setTaxExempt(!!d.taxExempt);
    }
    setSynced(true);
  }, [isSample, live]);

  // Re-attach uploaded PDF bytes for the provenance viewer. The cache lives in
  // doc-provenance-core.js at page-session scope (not in this component) so it
  // survives InputView remounting when you switch job tabs — otherwise tracing
  // falls back to re-fetching the storage URL cross-origin (needs bucket CORS).
  const rememberLocal = rememberLocalFile;
  const withLocal     = attachLocalFile;

  // Multi-trade jobs carry a quote per trade: the first (primary) trade keeps the
  // legacy `job.quote` field, the rest live under `job.quotesByTrade[trade]`.
  // Single-trade / untagged jobs use the plain `quote` path below, unchanged.
  const multiTrade   = trades.length > 1;
  const primaryTrade = trades[0] || null;
  // The trade the next upload is filed under — defaults to (and never lags) the
  // primary trade if the remembered one is gone.
  const effUploadTrade = multiTrade
    ? (uploadTrade && trades.includes(uploadTrade) ? uploadTrade : primaryTrade)
    : null;
  const quoteForTrade = (trade) =>
    trade === primaryTrade ? quote : (quotesByTrade[trade] ?? null);

  const saveQuote = (parsed) => {
    setSaveErr(null);
    rememberLocal(parsed);
    setQuote(parsed);
    if (!isSample) jobRef.update({ quote: clean(parsed) }).catch(handleWriteErr);
  };

  const clearQuote = () => {
    setSaveErr(null);
    setQuote(null);
    if (!isSample) jobRef.update({ quote: null }).catch(handleWriteErr);
  };

  // Per-trade quote write/clear. The primary trade routes through the legacy
  // `quote` field (so a single→multi-trade job keeps its existing quote as the
  // primary); other trades use a FieldPath so trade names with spaces/symbols
  // ("Sheet Metal") are stored as literal map keys, not dotted sub-paths.
  const saveQuoteForTrade = (trade, parsed) => {
    if (trade === primaryTrade) { saveQuote(parsed); return; }
    setSaveErr(null);
    rememberLocal(parsed);
    setQuotesByTrade((prev) => ({ ...prev, [trade]: parsed }));
    if (!isSample) {
      jobRef.update(new firebase.firestore.FieldPath('quotesByTrade', trade), clean(parsed))
        .catch(handleWriteErr);
    }
  };

  const clearQuoteForTrade = (trade) => {
    if (trade === primaryTrade) { clearQuote(); return; }
    setSaveErr(null);
    setQuotesByTrade((prev) => { const n = { ...prev }; delete n[trade]; return n; });
    if (!isSample) {
      jobRef.update(new firebase.firestore.FieldPath('quotesByTrade', trade),
        firebase.firestore.FieldValue.delete()).catch(handleWriteErr);
    }
  };

  const addInvoice = async (parsed) => {
    setSaveErr(null);
    rememberLocal(parsed);
    const entry = { ...clean(parsed), _addedAt: new Date().toISOString() };
    // On a multi-trade job, file the invoice under the chosen trade so it's
    // compared against that trade's quote (single-trade jobs stay untagged).
    if (multiTrade && effUploadTrade) entry.trade = effUploadTrade;
    // A multi-file drop calls this in a loop, so DON'T derive the new array from
    // the `invoices` closure — it stays frozen at its render-time value across
    // the loop, making every iteration overwrite the last (only the final file
    // would stick). Use a functional update locally and an independent-append
    // write (arrayUnion on legacy jobs, its own doc on split jobs) so each
    // invoice lands regardless of the others.
    setInvoices((prev) => [...prev, entry]);
    if (!isSample) invoiceStore.add(job.id, useSub, entry).catch(handleWriteErr);
  };

  const removeInvoice = (idx) => {
    setSaveErr(null);
    setInvoices(invoices.filter((_, i) => i !== idx));
    if (!isSample) invoiceStore.removeAt(job.id, useSub, invoices, idx).catch(handleWriteErr);
  };

  const toggleHideInvoice = (idx) => {
    setSaveErr(null);
    const next = { ...invoices[idx], _hidden: !invoices[idx]._hidden };
    setInvoices(invoices.map((inv, i) => (i === idx ? next : inv)));
    if (!isSample) invoiceStore.writeAt(job.id, useSub, invoices, idx, next).catch(handleWriteErr);
  };

  // Recover the original uploaded document for re-analysis: prefer the in-session
  // local file, else re-fetch the stored copy from its URL.
  const getInvoiceFile = async (inv) => {
    const local = withLocal(inv)._localFile;
    if (local) return local;
    if (inv._fileUrl) {
      const resp = await fetch(safeUrl(inv._fileUrl));
      if (!resp.ok) throw new Error('Could not fetch the original document.');
      const blob = await resp.blob();
      return new File([blob], inv._fileName || 'invoice.pdf', { type: blob.type || 'application/pdf' });
    }
    throw new Error('The original document is no longer available to re-analyze.');
  };

  // Re-run document analysis on an existing invoice — picks up improved parsing
  // (notably automatic credit-memo detection) and overwrites the parsed data,
  // preserving the row's identity, trade, and hidden state. Gated behind a
  // confirm because it calls the AI parser.
  const reAnalyze = async (idx) => {
    const inv = invoices[idx];
    setSaveErr(null);
    setReanalyzing(idx);
    try {
      const file   = await getInvoiceFile(inv);
      const parsed = await parseInvoiceFile(file);
      rememberLocal({ ...parsed, _fileUrl: inv._fileUrl, _fileName: inv._fileName });
      const merged = {
        ...clean(parsed),
        id: inv.id, _addedAt: inv._addedAt, _fileName: inv._fileName,
        ...(inv._fileUrl ? { _fileUrl: inv._fileUrl } : {}),
        ...(inv.trade   ? { trade:   inv.trade   } : {}),
        ...(inv._hidden ? { _hidden: true } : {}),
      };
      setInvoices(invoices.map((x, i) => (i === idx ? merged : x)));
      if (!isSample) await invoiceStore.writeAt(job.id, useSub, invoices, idx, merged).catch(handleWriteErr);
    } catch (e) {
      setSaveErr(e.message || 'Re-analysis failed.');
    } finally {
      setReanalyzing(null);
    }
  };

  const handleSubmitForApproval = async (inv) => {
    if (isSample || !userInfo) return;
    setSubmitErr(null);
    try {
      const docId = await window.submitForApproval(userId, userInfo, job.id, job.jobNumber, inv);
      setSubmitted((prev) => new Set([...prev, inv.id]));
    } catch (e) {
      setSubmitErr(e.message || 'Submission failed.');
    }
  };

  const [derivedProcoreUrl, setDerivedProcoreUrl] = React.useState(null);
  React.useEffect(() => {
    if (job.procoreUrl || !job.procoreProjectId) return undefined;
    let dead = false;
    // /api/procore now requires a Firebase ID token (audit #5).
    (async () => {
      const u = window.fbAuth?.currentUser;
      const headers = u ? { Authorization: `Bearer ${await u.getIdToken()}` } : {};
      return fetch('/api/procore?path=/_meta', { headers });
    })()
      .then((r) => (r.ok ? r.json() : {}))
      .then((meta) => {
        if (!dead && meta.companyId) {
          setDerivedProcoreUrl(
            `https://sandbox.procore.com/webclients/host/companies/${meta.companyId}/projects/${job.procoreProjectId}/tools/projecthome`
          );
        }
      })
      .catch(() => {});
    return () => { dead = true; };
  }, [job.procoreProjectId, job.procoreUrl]);

  const procoreUrl   = job.procoreUrl || derivedProcoreUrl;
  const vendor       = quote?.vendor || '';
  const historyCount = ((vendorRules || {})[vendor] || []).length;

  if (!synced) {
    return (
      <div className="iv-loading">
        <div className="iv-loading-spin"></div>
        <InputViewStyles />
      </div>
    );
  }

  return (
    <div className="input-view">
      <div className="iv-hero">
        <div className="iv-hero-top">
          <div className="iv-hero-tag mono">JOB {job.jobNumber} · INVOICES</div>
          {procoreUrl && (
            <a className="iv-procore-link" href={safeUrl(procoreUrl)} target="_blank" rel="noopener noreferrer">
              <Icon name="external" size={14} />
              Open in Procore
            </a>
          )}
        </div>
        <h1 className="iv-hero-h">Quote &amp; invoices.</h1>
        <p className="iv-hero-sub">
          Upload the quote that sets your baseline, then add invoices as they arrive.
          Budget totals roll up on the Summary tab.
        </p>
      </div>

      {/* UX #12: visible, dismissible save-failure notice (role=alert so SRs announce it). */}
      {saveErr && (
        <div role="alert" style={{
          display: 'flex', alignItems: 'center', gap: 8, margin: '0 0 16px',
          padding: '9px 12px', borderRadius: 'var(--r)', fontSize: 13,
          background: 'var(--bad-bg)', color: 'var(--bad)',
          border: '1px solid oklch(from var(--bad) l calc(c*.5) h / .4)',
        }}>
          <Icon name="flag" size={13} />
          <span style={{ flex: 1 }}>{saveErr}</span>
          <button className="btn btn-ghost btn-sm" onClick={() => setSaveErr(null)} aria-label="Dismiss">
            <Icon name="x" size={12} />
          </button>
        </div>
      )}

      <div className="iv-cols">
        {/* ── Quote sidebar — sticky baseline next to the wide invoice table ── */}
        <div className="iv-left">

          {/* Quote panel */}
          <div className="iv-panel card">
            <div className="iv-panel-hd">
              <span className="iv-panel-title">{multiTrade ? 'Quotes by trade' : 'Quote'}</span>
              {!multiTrade && quote && (
                <button className="btn btn-ghost btn-sm" onClick={() => setConfirmCfg({
                  title: 'Replace the quote?',
                  body: <>The current quote will be cleared so you can upload a new one. The budget tracker will update.</>,
                  confirmLabel: 'Replace quote', tone: 'warn', icon: 'reset',
                  onConfirm: clearQuote,
                })}>
                  <Icon name="reset" size={12} /> Replace
                </button>
              )}
            </div>
            <div className="iv-panel-body">
              {multiTrade ? (
                <div className="iv-qtrades">
                  {trades.map((trade) => {
                    const tq      = quoteForTrade(trade);
                    const tVendor = tq?.vendor || '';
                    const tHist   = ((vendorRules || {})[tVendor] || []).length;
                    return (
                      <div key={trade} className="iv-qtrade">
                        <div className="iv-qtrade-hd">
                          <span className="iv-qtrade-name">
                            <span className="iv-qtrade-dot" />{trade}
                            {trade === primaryTrade && <span className="iv-qtrade-tag">primary</span>}
                          </span>
                          {tq && (
                            <button className="btn btn-ghost btn-sm" onClick={() => setConfirmCfg({
                              title: `Replace the ${trade} quote?`,
                              body: <>The <strong>{trade}</strong> quote will be cleared so you can upload a new one. The budget tracker will update.</>,
                              confirmLabel: 'Replace quote', tone: 'warn', icon: 'reset',
                              onConfirm: () => clearQuoteForTrade(trade),
                            })}>
                              <Icon name="reset" size={12} /> Replace
                            </button>
                          )}
                        </div>
                        {tq ? (
                          <div className="quote-loaded">
                            <LoadedFileCard invoice={withLocal(tq)} side="A" />
                            {tHist > 0 && (
                              <div className="vendor-history vendor-history-on">
                                <div className="vh-icon"><Icon name="history" size={16} /></div>
                                <div>
                                  <div className="vh-title">
                                    <span className="num">{tHist}</span> historical adjustment{tHist !== 1 ? 's' : ''}
                                  </div>
                                  <div className="vh-sub">Applied to comparisons for <strong>{tVendor}</strong>.</div>
                                </div>
                              </div>
                            )}
                          </div>
                        ) : (
                          <DropZone side="A" label={`${trade} quote`} value={null}
                            onChange={(p) => saveQuoteForTrade(trade, p)} jobId={job.id} />
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : quote ? (
                <div className="quote-loaded">
                  <LoadedFileCard invoice={withLocal(quote)} side="A" />
                  {historyCount > 0 && (
                    <div className="vendor-history vendor-history-on">
                      <div className="vh-icon"><Icon name="history" size={16} /></div>
                      <div>
                        <div className="vh-title">
                          <span className="num">{historyCount}</span> historical adjustment{historyCount !== 1 ? 's' : ''}
                        </div>
                        <div className="vh-sub">Applied to comparisons for <strong>{vendor}</strong>.</div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <DropZone side="A" label="Quote / Baseline" value={null} onChange={saveQuote} jobId={job.id} />
              )}
            </div>
          </div>
        </div>

        {/* ── Invoices — the wide data table fills the rest of the width ──────── */}
        <div className="iv-main">
          {/* Invoices panel */}
          <div className="iv-panel card">
            <div className="iv-panel-hd">
              <span className="iv-panel-title">Invoices</span>
              {invoices.length > 0 && <span className="inv-count num">{invoices.length}</span>}
            </div>
            <div className="iv-panel-body">
              {/* Add invoice stays at the top so it's reachable without scrolling
                  past a long list. */}
              <div className="inv-add-hd">
                <span className="inv-add-label">Add Invoice</span>
                {multiTrade && (
                  <label className="inv-trade-pick">
                    <span className="inv-trade-pick-l">File under</span>
                    <select className="inv-select" value={effUploadTrade || ''}
                      onChange={(e) => setUploadTrade(e.target.value)} aria-label="Trade for new invoices">
                      {trades.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </label>
                )}
              </div>
              <AddInvoiceZone onAdd={addInvoice} jobId={job.id} />
              {submitErr && <div className="aiz-err">{submitErr}</div>}

              {invoices.length === 0 ? (
                <div className="inv-empty">
                  <div className="inv-empty-icon"><Icon name="file" size={22} /></div>
                  <div className="inv-empty-text">No invoices added yet.</div>
                </div>
              ) : (
                <>
                  {/* At-a-glance totals */}
                  <div className="inv-sum">
                    <div className="inv-sum-stat">
                      <span className="inv-sum-v num">{fmtUSD(invSummary.total)}</span>
                      <span className="inv-sum-l">Total invoiced{taxExempt ? ' · pre-tax' : ''}</span>
                    </div>
                    <div className="inv-sum-stat">
                      <span className="inv-sum-v num">{invSummary.count}</span>
                      <span className="inv-sum-l">Invoices{invSummary.hidden ? ` · ${invSummary.hidden} hidden` : ''}</span>
                    </div>
                    {invSummary.minD && (
                      <div className="inv-sum-stat">
                        <span className="inv-sum-v inv-sum-dates mono">{invSummary.minD === invSummary.maxD ? invSummary.minD : `${invSummary.minD} → ${invSummary.maxD}`}</span>
                        <span className="inv-sum-l">Date range</span>
                      </div>
                    )}
                  </div>

                  {/* Search + sort */}
                  <div className="inv-controls">
                    <div className="inv-search">
                      <Icon name="search" size={13} />
                      <input className="inv-search-input" placeholder="Search file, invoice #, vendor, date…"
                        value={invQuery} onChange={(e) => setInvQuery(e.target.value)} aria-label="Search invoices" />
                      {invQuery && <button className="inv-clear" onClick={() => setInvQuery('')} aria-label="Clear search"><Icon name="x" size={11} /></button>}
                    </div>
                    <select className="inv-select" value={invSort} onChange={(e) => setInvSort(e.target.value)} aria-label="Sort invoices">
                      {INV_SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                    </select>
                  </div>

                  {invView.length === 0 ? (
                    <div className="inv-empty"><div className="inv-empty-text">No invoices match your search.</div></div>
                  ) : (
                    <div className="inv-tblwrap">
                      <div className="inv-tbl" role="table">
                        <div className="inv-tr inv-tr-head" role="row">
                          <span role="columnheader" className="inv-reanalyze-h" aria-label="Re-analyze"></span>
                          <span role="columnheader">Date</span>
                          <span role="columnheader">Invoice</span>
                          <span role="columnheader">Vendor</span>
                          <span role="columnheader" className="inv-num">Items</span>
                          <span role="columnheader" className="inv-num">Total</span>
                          <span role="columnheader" className="inv-acts-h">Actions</span>
                        </div>
                        {(invShowAll ? invView : invView.slice(0, INV_PAGE)).map(({ inv, idx }) => (
                          <div key={inv._addedAt ?? inv.id} id={`invrow-${inv.id}`}
                            className={`inv-tr${inv._hidden ? ' inv-tr-hidden' : ''}${highlightInv && inv.id === highlightInv ? ' inv-tr-focus' : ''}`} role="row">
                            <span role="cell" className="inv-reanalyze-cell">
                              {(withLocal(inv)._localFile || inv._fileUrl) && (
                                <button className="btn btn-ghost btn-sm" title="Re-analyze the original document (auto-detects credits)" aria-label="Re-analyze invoice"
                                  disabled={reanalyzingIdx != null}
                                  onClick={() => setConfirmCfg({
                                    title: 'Re-analyze this invoice?',
                                    body: <>This re-runs document analysis on &ldquo;<strong>{inv._fileName || inv.id}</strong>&rdquo; and overwrites its parsed data (credit memos are detected automatically). It uses the AI parser, so re-run it only when needed.</>,
                                    confirmLabel: 'Re-analyze', tone: 'warn', icon: 'reset',
                                    onConfirm: () => reAnalyze(idx),
                                  })}>
                                  {reanalyzingIdx === idx ? <span className="inv-reanalyze-spin" /> : <Icon name="reset" size={12} />}
                                </button>
                              )}
                            </span>
                            <span role="cell" className="inv-date mono">{inv.date || '—'}</span>
                            <span role="cell" className="inv-file" title={inv._fileName || inv.id}>
                              <span className="inv-file-name">{inv._fileName || inv.id}</span>
                              {inv.isCredit && <span className="inv-credit-badge" title="Credit memo — reduces total spent">CREDIT</span>}
                              {inv._parseWarn && <span className="inv-warn" title={`Parsed ${fmtUSD(inv._parseWarn.computedSubtotal)} vs stated ${fmtUSD(inv._parseWarn.statedSubtotal)} — prices may be off`}><Icon name="flag" size={10} /></span>}
                              {inv._hidden && <span className="inv-hidden-badge">hidden</span>}
                            </span>
                            <span role="cell" className="inv-vendor" title={inv.vendor}>
                              {inv.vendor || '—'}
                              {multiTrade && inv.trade && <span className="inv-trade-badge">{inv.trade}</span>}
                            </span>
                            <span role="cell" className="inv-num inv-dim">{inv.items.length}</span>
                            <span role="cell" className={`inv-num inv-total${invoiceTotal(inv, taxExempt) < 0 ? ' inv-total-credit' : ''}`}>{fmtUSD(invoiceTotal(inv, taxExempt))}</span>
                            <span role="cell" className="inv-acts">
                              {pdfSourceOf(withLocal(inv)) && (
                                <button className="btn btn-ghost btn-sm" title="Trace items on the original PDF" aria-label="Trace items on the original PDF" onClick={() => setProvIdx(idx)}><Icon name="search" size={12} /></button>
                              )}
                              {inv._fileUrl && (
                                <a className="btn btn-ghost btn-sm" href={safeUrl(inv._fileUrl)} target="_blank" rel="noopener noreferrer" title="Open original file" aria-label="Open original file"><Icon name="external" size={12} /></a>
                              )}
                              {window.APPROVALS_ENABLED && !isSample && userInfo && !inv._hidden && (
                                submittedIds.has(inv.id)
                                  ? <span className="inv-submitted-badge" title="Submitted"><Icon name="check" size={11} /></span>
                                  : <button className="btn btn-sm inv-submit-btn" title="Submit for approval" aria-label="Submit for approval" onClick={() => handleSubmitForApproval(inv)}><Icon name="arrow" size={11} /></button>
                              )}
                              <button className="btn btn-ghost btn-sm" title={inv._hidden ? 'Unhide invoice' : 'Hide from budget'} aria-label={inv._hidden ? 'Unhide invoice' : 'Hide invoice from budget'} onClick={() => toggleHideInvoice(idx)}><Icon name={inv._hidden ? 'eye' : 'eye-off'} size={12} /></button>
                              <button className="btn btn-ghost btn-sm" title="Remove invoice" aria-label="Remove invoice"
                                onClick={() => setConfirmCfg({
                                  title: 'Remove this invoice?',
                                  body: <>&ldquo;<strong>{inv._fileName || inv.id}</strong>&rdquo; will be removed from this job and its budget. This can&rsquo;t be undone.</>,
                                  confirmLabel: 'Remove invoice',
                                  tone: 'bad', icon: 'trash',
                                  onConfirm: () => removeInvoice(idx),
                                })}><Icon name="x" size={12} /></button>
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="inv-foot">
                    <span className="inv-dim">
                      {(invShowAll || invView.length <= INV_PAGE)
                        ? `Showing ${invView.length}`
                        : `Showing ${INV_PAGE} of ${invView.length}`}{invQuery ? ' matching' : ''}
                      {invView.length > INV_PAGE && (
                        <button className="inv-showall" onClick={() => setInvShowAll((v) => !v)}>
                          {invShowAll ? 'Show fewer' : `Show all ${invView.length}`}
                        </button>
                      )}
                    </span>
                    <span className="inv-foot-total num">Total invoiced <strong>{fmtUSD(invSummary.total)}</strong></span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {provIdx != null && invoices[provIdx] && (
        <DocProvenanceModal invoice={withLocal(invoices[provIdx])} onClose={() => setProvIdx(null)} />
      )}

      {confirmCfg && <ConfirmModal cfg={confirmCfg} onClose={() => setConfirmCfg(null)} />}

      <InputViewStyles />
    </div>
  );
}

const InputViewStyles = () => (
  <style>{`
    /* ── Layout ──────────────────────────────────────────────── */
    .input-view { width: 100%; max-width: var(--app-max); margin: 0 auto; padding: 40px var(--app-pad) 80px; }
    .iv-loading { display: flex; align-items: center; justify-content: center; min-height: 60vh; }
    .iv-loading-spin { width: 28px; height: 28px; border-radius: 50%; border: 2.5px solid var(--border); border-top-color: var(--accent); animation: dz-spin .7s linear infinite; }

    .iv-hero { margin-bottom: 22px; }
    .iv-hero-top { display: flex; align-items: center; justify-content: space-between; margin-bottom: 14px; gap: 16px; flex-wrap: wrap; }
    .iv-hero-tag { font-size: 11px; font-weight: 600; letter-spacing: 0.1em; text-transform: uppercase; color: var(--accent); }
    .iv-procore-link {
      display: inline-flex; align-items: center; gap: 7px;
      padding: 7px 14px; border-radius: var(--r);
      background: oklch(0.62 0.12 250 / 0.1); border: 1px solid oklch(0.62 0.12 250 / 0.3);
      color: oklch(0.62 0.12 250); font-size: 13px; font-weight: 500;
      text-decoration: none; transition: background .12s, border-color .12s;
    }
    .iv-procore-link:hover { background: oklch(0.62 0.12 250 / 0.18); border-color: oklch(0.62 0.12 250 / 0.5); }
    .iv-hero-h { font-size: 25px; line-height: 1.15; letter-spacing: -0.02em; margin: 0 0 8px; font-weight: 600; }
    .iv-hero-sub { color: var(--fg-muted); font-size: 14.5px; line-height: 1.55; margin: 0; max-width: 560px; }

    /* Two-column working layout: a sticky quote sidebar beside the wide invoice
       table, so the table uses the full width instead of a centered column. */
    .iv-cols { display: grid; grid-template-columns: minmax(320px, 400px) minmax(0, 1fr); gap: 24px; align-items: start; }
    .iv-left  { display: flex; flex-direction: column; gap: 16px; position: sticky; top: 116px; align-self: start; }
    .iv-main  { min-width: 0; }
    .iv-right { position: sticky; top: 76px; }
    .iv-budget-label { font-size: 10.5px; font-weight: 600; letter-spacing: 0.08em; text-transform: uppercase; color: var(--fg-dim); margin-bottom: 8px; }

    .iv-panel { display: flex; flex-direction: column; }
    .iv-panel-hd { display: flex; align-items: center; gap: 10px; padding: 12px 16px; border-bottom: 1px solid var(--border); }
    .iv-panel-title { font-size: 13px; font-weight: 600; letter-spacing: -0.005em; flex: 1; }
    .iv-panel-body { padding: 16px; display: flex; flex-direction: column; gap: 12px; }
    .inv-count { font-size: 11px; padding: 2px 8px; border-radius: 999px; background: oklch(from var(--accent) l c h / 0.15); color: var(--accent); }

    /* ── Quote panel ──────────────────────────────────────────── */
    .quote-loaded { display: flex; flex-direction: column; gap: 10px; }

    /* ── Per-trade quotes (multi-trade jobs) ──────────────────── */
    .iv-qtrades { display: flex; flex-direction: column; gap: 16px; }
    .iv-qtrade { display: flex; flex-direction: column; gap: 10px; }
    .iv-qtrade + .iv-qtrade { padding-top: 16px; border-top: 1px solid var(--border); }
    .iv-qtrade-hd { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
    .iv-qtrade-name { display: inline-flex; align-items: center; gap: 7px; font-size: 13px; font-weight: 600; }
    .iv-qtrade-dot { width: 7px; height: 7px; border-radius: 50%; background: var(--accent); flex-shrink: 0; }
    .iv-qtrade-tag {
      font-size: 9.5px; font-weight: 600; letter-spacing: .04em; text-transform: uppercase;
      color: var(--fg-dim); background: var(--surface-3); border: 1px solid var(--border);
      padding: 2px 6px; border-radius: 4px;
    }

    /* ── Invoice list ─────────────────────────────────────────── */
    .inv-empty { display: flex; align-items: center; gap: 10px; padding: 6px 0; }
    .inv-empty-icon { color: var(--fg-dim); opacity: 0.35; }
    .inv-empty-text { font-size: 13px; color: var(--fg-dim); }
    /* Invoice summary strip */
    .inv-sum { display: grid; grid-template-columns: repeat(auto-fit, minmax(120px, 1fr)); gap: 10px; }
    .inv-sum-stat { background: var(--surface-2); border: 1px solid var(--border); border-radius: 10px; padding: 10px 14px; display: flex; flex-direction: column; gap: 3px; }
    .inv-sum-v { font-size: 19px; font-weight: 680; line-height: 1.1; }
    .inv-sum-dates { font-size: 13px; font-weight: 500; }
    .inv-sum-l { font-size: 10.5px; text-transform: uppercase; letter-spacing: .03em; color: var(--fg-dim); }

    /* Invoice search + sort */
    .inv-controls { display: flex; gap: 10px; flex-wrap: wrap; }
    .inv-search { flex: 1 1 220px; display: flex; align-items: center; gap: 7px; padding: 0 10px; background: var(--surface-2); border: 1px solid var(--border); border-radius: 8px; color: var(--fg-dim); }
    .inv-search-input { flex: 1; min-width: 0; background: none; border: none; outline: none; color: var(--fg); font-size: 13px; padding: 8px 0; }
    .inv-clear { background: none; border: none; color: var(--fg-dim); cursor: pointer; display: flex; padding: 2px; }
    .inv-select { background: var(--surface-2); border: 1px solid var(--border); border-radius: 8px; color: var(--fg); font-size: 12.5px; padding: 0 10px; height: 36px; cursor: pointer; }

    /* Invoice table */
    .inv-tblwrap { overflow-x: auto; border: 1px solid var(--border); border-radius: 10px; background: var(--surface-1); }
    .inv-tbl { min-width: 596px; }
    .inv-tr { display: grid; grid-template-columns: 34px 92px minmax(120px,1.6fr) minmax(90px,1.2fr) 52px 96px auto;
      gap: 10px; align-items: center; padding: 9px 14px; border-bottom: 1px solid var(--border); font-size: 13px; }
    .inv-tr:last-child { border-bottom: none; }
    .inv-tr-head { font-size: 10px; text-transform: uppercase; letter-spacing: .05em; color: var(--fg-dim); background: var(--surface-2); position: sticky; top: 0; z-index: 1; }
    .inv-tr:not(.inv-tr-head):hover { background: var(--surface-2); }
    .inv-tr-hidden { opacity: 0.5; }
    .inv-tr-focus { animation: inv-pulse 2.4s ease-out; }
    @keyframes inv-pulse {
      0%, 18% { background: oklch(from var(--accent) l c h / 0.22); box-shadow: inset 3px 0 0 var(--accent); }
      100% { background: transparent; box-shadow: inset 3px 0 0 transparent; }
    }
    .inv-reanalyze-cell { display: flex; align-items: center; justify-content: center; }
    .inv-reanalyze-cell .btn { padding: 4px; }
    .inv-date { color: var(--fg-muted); font-size: 12px; white-space: nowrap; }
    .inv-file { display: flex; align-items: center; gap: 6px; min-width: 0; }
    .inv-file-name { font-weight: 500; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .inv-warn { color: var(--warn); display: inline-flex; flex-shrink: 0; }
    .inv-vendor { color: var(--fg-muted); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .inv-num { text-align: right; font-variant-numeric: tabular-nums; }
    .inv-dim { color: var(--fg-dim); }
    .inv-total { font-weight: 650; }
    .inv-acts-h { text-align: right; }
    .inv-acts { display: flex; gap: 4px; justify-content: flex-end; }

    .inv-foot { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; padding-top: 4px; font-size: 12.5px; }
    .inv-foot-total { color: var(--fg-muted); }
    .inv-foot-total strong { color: var(--fg); font-weight: 700; }
    .inv-showall { background: none; border: none; color: var(--accent); font-size: 12.5px; font-weight: 500; cursor: pointer; padding: 0 0 0 8px; }
    .inv-showall:hover { text-decoration: underline; }
    .inv-hidden-badge {
      font-size: 10px; padding: 1px 7px; border-radius: 999px;
      background: var(--surface-3); color: var(--fg-dim);
      border: 1px solid var(--border); font-weight: 500;
      text-transform: uppercase; letter-spacing: 0.05em;
    }
    .inv-add-hd { display: flex; align-items: center; justify-content: space-between; gap: 10px; padding: 0 0 8px; }
    .inv-add-label { font-size: 11px; font-weight: 500; letter-spacing: 0.04em; text-transform: uppercase; color: var(--fg-dim); }
    .inv-trade-pick { display: inline-flex; align-items: center; gap: 7px; }
    .inv-trade-pick-l { font-size: 11px; color: var(--fg-dim); }
    .inv-trade-badge {
      display: inline-block; margin-left: 6px; font-size: 9.5px; font-weight: 600;
      letter-spacing: .02em; line-height: 1; padding: 2px 6px; border-radius: 4px;
      background: oklch(from var(--accent) l c h / .12); color: var(--accent);
      border: 1px solid oklch(from var(--accent) l c h / .3); vertical-align: middle;
    }
    /* Credit memo: badge + green (money back) total + active toggle state. */
    .inv-credit-badge {
      display: inline-block; margin-left: 6px; font-size: 9px; font-weight: 700;
      letter-spacing: .04em; line-height: 1; padding: 2px 6px; border-radius: 4px;
      background: var(--ok-bg); color: var(--ok);
      border: 1px solid oklch(from var(--ok) l calc(c*.5) h / .4); vertical-align: middle;
    }
    .inv-total-credit { color: var(--ok); }
    .inv-reanalyze-spin { display: inline-block; width: 12px; height: 12px; border-radius: 50%;
      border: 2px solid var(--border); border-top-color: var(--accent); animation: dz-spin .7s linear infinite; }
    .loaded-parse-warn { display: flex; align-items: flex-start; gap: 6px; padding: 7px 10px; border-radius: var(--r); background: var(--warn-bg); color: var(--warn); border: 1px solid oklch(from var(--warn) l calc(c*.5) h / .35); font-size: 11.5px; line-height: 1.45; }
    .inv-parse-warn { display: inline-flex; align-items: center; gap: 3px; font-size: 11px; color: var(--warn); font-weight: 500; }
    .inv-submit-btn { border-color: oklch(from var(--accent) l calc(c*.6) h / .5); color: var(--accent); }
    .inv-submit-btn:hover { background: oklch(from var(--accent) l c h / 0.1); border-color: var(--accent); }
    .inv-submitted-badge { display: inline-flex; align-items: center; gap: 4px; font-size: 11px; color: var(--ok); padding: 2px 8px; }

    /* ── AddInvoiceZone ───────────────────────────────────────── */
    .aiz {
      border: 1.5px dashed var(--border-strong); border-radius: var(--r-lg);
      padding: 14px; background: var(--surface);
      display: flex; align-items: center; justify-content: center;
      flex-wrap: wrap; gap: 10px;
      transition: border-color .15s, background .15s; min-height: 60px;
    }
    .aiz.is-over { border-color: var(--accent); background: oklch(from var(--accent) l c h / 0.08); }
    .aiz.is-parsing { pointer-events: none; opacity: 0.75; }
    .aiz-spin { width: 20px; height: 20px; border-radius: 50%; border: 2px solid var(--border); border-top-color: var(--accent); animation: dz-spin .7s linear infinite; }
    .aiz-hint { font-size: 12.5px; color: var(--fg-muted); }
    .aiz-paste { width: 100%; min-height: 72px; font-family: "IBM Plex Mono", monospace; font-size: 12px; }
    .aiz-err { width: 100%; font-size: 12px; color: var(--bad); background: var(--bad-bg); border: 1px solid oklch(from var(--bad) l calc(c*.5) h / .35); border-radius: var(--r); padding: 6px 10px; }

    /* ── DropZone + LoadedFileCard ────────────────────────────── */
    .dropzone-wrap { display: flex; flex-direction: column; min-height: 200px; }
    .dz-hd { display: flex; align-items: center; justify-content: space-between; padding-bottom: 10px; }
    .dz-tag { display: flex; align-items: center; gap: 10px; }
    .dz-letter { width: 26px; height: 26px; border-radius: 6px; display: grid; place-items: center; font-family: "IBM Plex Mono", monospace; font-weight: 600; font-size: 12px; }
    .dz-label { font-size: 13px; font-weight: 500; }
    .dz-sub { font-size: 11.5px; color: var(--fg-dim); margin-top: 1px; }
    .dropzone {
      flex: 1; border: 1.5px dashed var(--border-strong); border-radius: var(--r-lg);
      padding: 32px 18px; background: var(--surface);
      display: flex; flex-direction: column; align-items: center; justify-content: center;
      gap: 12px; transition: border-color .15s, background .15s;
    }
    .dropzone.is-over { border-color: var(--accent); background: oklch(from var(--accent) l c h / 0.08); }
    .dz-icon-wrap { width: 40px; height: 40px; border-radius: 50%; background: var(--surface-2); border: 1px solid var(--border); display: grid; place-items: center; color: var(--fg-muted); }
    .dz-title { font-size: 13px; font-weight: 500; color: var(--fg); text-align: center; }
    .dz-or { font-size: 11px; color: var(--fg-dim); letter-spacing: 0.08em; text-transform: uppercase; }
    .dz-actions { display: flex; gap: 8px; flex-wrap: wrap; justify-content: center; }
    .dz-paste { margin-top: 12px; width: 100%; min-height: 80px; font-family: "IBM Plex Mono", monospace; font-size: 12px; }
    .dz-hint { font-size: 12px; color: var(--fg-dim); }
    .dz-error { font-size: 12px; color: var(--bad); background: var(--bad-bg); border: 1px solid oklch(from var(--bad) l calc(c*.5) h / .35); border-radius: var(--r); padding: 6px 10px; max-width: 300px; text-align: center; line-height: 1.5; }
    .dropzone.is-parsing { pointer-events: none; opacity: 0.8; }
    .dz-spinner { width: 28px; height: 28px; border-radius: 50%; border: 2.5px solid var(--border); border-top-color: var(--accent); animation: dz-spin .7s linear infinite; }
    @keyframes dz-spin { to { transform: rotate(360deg); } }
    .loaded { flex: 1; background: var(--surface); border: 1px solid var(--border); border-radius: var(--r-lg); box-shadow: var(--shadow-1); padding: 14px 16px; display: flex; flex-direction: column; gap: 12px; }
    .loaded-hd { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
    .loaded-hd-r { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
    .loaded-raw-btn { font-size: 11px; gap: 5px; }
    .loaded-raw-btn.is-on { color: var(--accent); background: oklch(from var(--accent) l c h / 0.1); }
    .loaded-id { display: flex; align-items: center; gap: 8px; font-size: 13px; color: var(--fg); font-weight: 500; }
    .loaded-id .mono { color: var(--fg-muted); font-size: 12px; }
    .loaded-raw { display: flex; flex-direction: column; gap: 6px; }
    .loaded-raw-label { font-size: 10px; text-transform: uppercase; letter-spacing: 0.08em; color: var(--fg-dim); font-weight: 500; }
    .loaded-raw-pre {
      margin: 0; padding: 10px 12px;
      background: var(--surface-3); border: 1px solid var(--border); border-radius: var(--r);
      font-family: "IBM Plex Mono", monospace; font-size: 10.5px; line-height: 1.55;
      color: var(--fg-muted); white-space: pre-wrap; word-break: break-all;
      max-height: 300px; overflow-y: auto;
    }
    .loaded-rows { display: flex; flex-direction: column; gap: 5px; padding-top: 10px; border-top: 1px solid var(--border); }
    .lr { display: flex; justify-content: space-between; align-items: baseline; font-size: 12px; }
    .lr-k { color: var(--fg-dim); }
    .lr-v { color: var(--fg); font-weight: 500; }
    .lr-total { padding-top: 6px; margin-top: 4px; border-top: 1px dashed var(--border); }
    .lr-total .lr-v { font-size: 13.5px; }
    .loaded-preview { background: var(--surface-2); border-radius: var(--r); padding: 9px 11px; display: flex; flex-direction: column; gap: 4px; }
    .lp-row { display: grid; grid-template-columns: 72px 1fr 64px; gap: 10px; font-size: 11px; align-items: baseline; }
    .lp-more { font-size: 10.5px; color: var(--fg-dim); padding-top: 4px; border-top: 1px dashed var(--border); margin-top: 4px; }
    .vendor-history { display: flex; gap: 10px; align-items: flex-start; padding: 10px 12px; border-radius: var(--r); border: 1px dashed var(--border-strong); }
    .vendor-history-on { background: var(--info-bg); border: 1px solid oklch(from var(--info) l calc(c*.4) h / .35); }
    .vh-icon { width: 30px; height: 30px; border-radius: 7px; background: oklch(from var(--info) l c h / .22); color: var(--info); display: grid; place-items: center; flex-shrink: 0; }
    .vh-title { font-size: 12.5px; font-weight: 500; }
    .vh-title .num { color: var(--info); font-weight: 600; }
    .vh-sub { font-size: 11.5px; color: var(--fg-dim); margin-top: 2px; line-height: 1.45; }

    @media (max-width: 1000px) { .iv-cols { grid-template-columns: 1fr; } .iv-left { position: static; } .iv-right { position: static; } }
  `}</style>
);

// LoadedFileCard is exported for tests/invodiff-input.test.jsx — it is the
// first place a user sees a parsed document's grand total, so it is worth
// pinning directly. Nothing else imports it.
export { Icon, InputView, DropZone, LoadedFileCard };